import React, { useRef } from "react";
import SmartImage from "@/components/SmartImage";
import { ChevronLeft, ChevronRight, Factory } from "lucide-react";

/**
 * ManufacturingSlider — premium horizontal, swipeable factory/assembly gallery.
 * Uses native scroll-snap (smooth touch) + arrow controls.
 *
 * Props:
 *  - items: [{ image, title, description }]
 *  - eyebrow, heading, subtitle (section header; omit to render bare slider)
 *  - showText: show title/description under each image (default true)
 *  - background: section background css value
 */
export default function ManufacturingSlider({
  items = [],
  eyebrow = "Inside Our Factory",
  heading = "Manufacturing & Assembly",
  subtitle = "",
  showText = true,
  background = "var(--bg-2)",
  id = "manufacturing",
}) {
  const trackRef = useRef(null);
  const clean = (items || []).filter((it) => it && (it.image || it.title));
  if (clean.length === 0) return null;

  const scrollByCards = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-card]");
    const amount = card ? card.getBoundingClientRect().width + 20 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <section className="ekta-section ekta-mfg-slider" id={id} style={{ background }} data-testid="manufacturing-slider">
      <div className="ekta-container">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4 sm:gap-4 sm:mb-8">
          <div className="max-w-2xl">
            <div className="ekta-eyebrow inline-flex items-center gap-2" style={{ color: "var(--yellow-dark)" }}>
              <Factory size={15} /> {eyebrow}
            </div>
            <h2 className="ekta-h2 mt-2">{heading}</h2>
            {subtitle && <p className="mt-2 text-[13px] sm:mt-3 sm:text-sm" style={{ color: "var(--muted-ink)", lineHeight: 1.6 }}>{subtitle}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
          {clean.map((it, i) => (
            <figure
              key={i}
              data-card
              data-testid="manufacturing-card"
              className="rounded-xl overflow-hidden bg-white border flex flex-row sm:flex-col transition-shadow hover:shadow-[var(--shadow-md)]"
              style={{ borderColor: "var(--border-c)", boxShadow: "var(--shadow-sm)" }}
            >
              <div className="relative overflow-hidden shrink-0 w-[42%] sm:w-full" style={{ aspectRatio: "4 / 3", background: "#0e0f14" }}>
                {it.image ? (
                  <SmartImage src={it.image} alt={it.title || `Manufacturing ${i + 1}`} className="w-full h-full object-cover sm:object-contain" sizes="(max-width: 640px) 42vw, 33vw" widths={[400, 640, 840]} />
                ) : (
                  <div className="w-full h-full grid place-items-center" style={{ color: "var(--muted-ink)" }}><Factory size={40} /></div>
                )}
                <span className="absolute top-2 left-2 sm:top-3 sm:left-3 h-7 w-7 sm:h-8 sm:w-8 rounded-full grid place-items-center text-[11px] sm:text-xs font-bold text-white" style={{ background: "var(--brand-primary)" }}>{String(i + 1).padStart(2, "0")}</span>
              </div>
              {showText && (it.title || it.description) && (
                <figcaption className="p-3.5 sm:p-5 flex-1 flex flex-col justify-center sm:block">
                  {it.title && <h3 className="font-bold text-[15px] sm:text-base" style={{ fontFamily: "var(--font-heading)", color: "var(--ink)" }}>{it.title}</h3>}
                  {it.description && <div className="mt-1.5 text-[12.5px] sm:mt-2 sm:text-sm prose-ekta ekta-assembly-desc" style={{ color: "var(--muted-ink)", lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: it.description }} />}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
